/* eslint-disable @typescript-eslint/no-invalid-void-type */
/** Billing/subscription hooks + CRM/channel capability metadata. */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useApiClient } from '../context';
import type { ApiError } from '../hc';
import { deleteJson, getJson, postJson } from './_http';

/* ─── Billing — Wave 7 (FULLY IMPLEMENTED) ─────────────────────────────── */

export type BillingPlan = 'discover' | 'pro' | 'scale' | 'agency';
export type BillingCycle = 'monthly' | 'yearly';
export type BillingStatus =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'unpaid';

export interface BillingSubscriptionRow {
  id: string;
  plan: BillingPlan;
  status: BillingStatus;
  stripeSubscriptionId?: string | null;
  trialEndsAt: number | null;
  currentPeriodStart: number | null;
  currentPeriodEnd: number | null;
  cancelAtPeriodEnd: boolean;
  paymentMethodBrand: string | null;
  paymentMethodLast4: string | null;
}

export interface BillingTrialEnvelope {
  status: BillingStatus;
  plan: BillingPlan;
  trialEndsAt: number | null;
  trialDaysLeft: number | null;
  bannerState: 'normal' | 'urgent' | 'endingToday' | 'expired' | null;
  accessTier: 'full' | 'soft_block' | 'hard_block';
  expired: boolean;
}

/** MTD AI usage vs. plan caps — `null` limit = unlimited (agency). */
export interface BillingUsage {
  aiCallsUsed: number;
  aiCallsLimit: number | null;
  aiBudgetUsedCents: number;
  aiBudgetLimitCents: number | null;
}

export interface BillingResponse {
  subscription: BillingSubscriptionRow | null;
  trial: BillingTrialEnvelope;
  limits: {
    accounts: number;
    activeCampaigns: number;
    aiCallsPerMonth: number;
    aiBudgetCentsPerMonth: number;
  };
  /** MTD AI usage (optional so older fixtures/responses stay valid). */
  usage?: BillingUsage;
  /** Convenience derived field for `<TrialBanner />` consumers. */
  trialDaysLeft?: number;
}

export interface BillingPrice {
  plan: BillingPlan;
  cycle: BillingCycle;
  active: boolean;
  /** Stripe `unit_amount`, expressed in the currency's minor unit. */
  unitAmount: number;
  currency: string;
  interval: 'day' | 'week' | 'month' | 'year';
  intervalCount: number;
}

export interface BillingPricesResponse {
  prices: BillingPrice[];
  /** Purchase cycles with at least one active, server-validated Stripe Price. */
  availableCycles?: BillingCycle[];
}

export const billingPricesKey = ['billing', 'prices'] as const;

/** `GET /api/v1/billing/prices` — amounts and currencies resolved from Stripe. */
export function useBillingPrices(
  options: { enabled?: boolean } = {},
): UseQueryResult<BillingPricesResponse, ApiError> {
  const client = useApiClient();
  return useQuery<BillingPricesResponse, ApiError>({
    queryKey: billingPricesKey,
    queryFn: () => getJson<BillingPricesResponse>(client, '/api/v1/billing/prices'),
    enabled: options.enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export const billingSubscriptionKey = ['billing', 'subscription'] as const;

/** `GET /api/v1/billing/subscription` */
export function useBilling(
  options: { enabled?: boolean; refetchInterval?: number } = {},
): UseQueryResult<BillingResponse, ApiError> {
  const client = useApiClient();
  return useQuery<BillingResponse, ApiError>({
    queryKey: billingSubscriptionKey,
    queryFn: async () => {
      const data = await getJson<BillingResponse>(client, '/api/v1/billing/subscription');
      // Mirror trial.trialDaysLeft into the top-level field consumed by the
      // shell trial banner (it's been reading this since Wave 5).
      return {
        ...data,
        trialDaysLeft: data.trial.trialDaysLeft ?? undefined,
      };
    },
    enabled: options.enabled,
    refetchInterval: options.refetchInterval,
  });
}

/** Alias preferred in feature components. */
export const useSubscription = useBilling;

export interface InvoiceRow {
  id: string;
  stripeInvoiceId: string | null;
  number: string;
  amountCents: number;
  currency: string;
  status: 'paid' | 'open' | 'void' | 'uncollectible' | 'draft';
  hostedInvoiceUrl: string | null;
  pdfUrl: string | null;
  periodStart: number | null;
  periodEnd: number | null;
  createdAt: number;
}

export interface InvoicesResponse {
  items: InvoiceRow[];
  nextCursor: string | null;
}

export const invoicesKey = ['billing', 'invoices'] as const;

/** `GET /api/v1/billing/invoices` */
export function useInvoices(
  options: { limit?: number } = {},
): UseQueryResult<InvoicesResponse, ApiError> {
  const client = useApiClient();
  const limit = options.limit ?? 20;
  return useQuery<InvoicesResponse, ApiError>({
    queryKey: [...invoicesKey, limit],
    queryFn: () => getJson<InvoicesResponse>(client, `/api/v1/billing/invoices?limit=${limit}`),
  });
}

export interface CheckoutResponse {
  url: string | null;
  sessionId: string;
}

/** `POST /api/v1/billing/checkout` — returns the Stripe Checkout URL. */
export function useStartCheckout(): UseMutationResult<
  CheckoutResponse,
  ApiError,
  { plan: 'pro' | 'scale' | 'agency'; cycle?: BillingCycle; trialDays?: number }
> {
  const client = useApiClient();
  return useMutation<
    CheckoutResponse,
    ApiError,
    { plan: 'pro' | 'scale' | 'agency'; cycle?: BillingCycle; trialDays?: number }
  >({
    mutationFn: (vars) => postJson<CheckoutResponse>(client, '/api/v1/billing/checkout', vars),
  });
}

export interface PortalResponse {
  url: string;
}

/** `POST /api/v1/billing/portal` */
export function useStartBillingPortal(): UseMutationResult<
  PortalResponse,
  ApiError,
  { returnTo?: string } | void
> {
  const client = useApiClient();
  return useMutation<PortalResponse, ApiError, { returnTo?: string } | void>({
    mutationFn: (vars) => postJson<PortalResponse>(client, '/api/v1/billing/portal', vars ?? {}),
  });
}

/** `POST /api/v1/billing/change-plan` (upgrade or paid → paid downgrade). */
export function useUpgradeSubscription(): UseMutationResult<
  { subscription: BillingSubscriptionRow },
  ApiError,
  { plan: BillingPlan; cycle?: BillingCycle }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { subscription: BillingSubscriptionRow },
    ApiError,
    { plan: BillingPlan; cycle?: BillingCycle }
  >({
    mutationFn: (vars) =>
      postJson<{ subscription: BillingSubscriptionRow }>(
        client,
        '/api/v1/billing/change-plan',
        vars,
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingSubscriptionKey });
    },
  });
}

/** Alias used by downgrade flow. */
export const useDowngradeSubscription = useUpgradeSubscription;

/** `POST /api/v1/billing/cancel` */
export function useCancelSubscription(): UseMutationResult<
  { subscription: BillingSubscriptionRow; atPeriodEnd: boolean },
  ApiError,
  { atPeriodEnd?: boolean; reason?: string } | void
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<
    { subscription: BillingSubscriptionRow; atPeriodEnd: boolean },
    ApiError,
    { atPeriodEnd?: boolean; reason?: string } | void
  >({
    mutationFn: (vars) =>
      postJson<{ subscription: BillingSubscriptionRow; atPeriodEnd: boolean }>(
        client,
        '/api/v1/billing/cancel',
        vars ?? { atPeriodEnd: true },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingSubscriptionKey });
    },
  });
}

/** `POST /api/v1/billing/cancel-trial` */
export function useCancelTrial(): UseMutationResult<
  { subscription: BillingSubscriptionRow },
  ApiError,
  void
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<{ subscription: BillingSubscriptionRow }, ApiError, void>({
    mutationFn: () =>
      postJson<{ subscription: BillingSubscriptionRow }>(
        client,
        '/api/v1/billing/cancel-trial',
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingSubscriptionKey });
    },
  });
}

export interface SetupIntentResponse {
  clientSecret: string;
  setupIntentId: string;
  publishableKey: string | null;
}

export interface StartTrialResponse {
  subscription: BillingSubscriptionRow;
  trial: BillingTrialEnvelope;
}

export interface StartTrialInput {
  /** Onboarding sells discover / scale / agency (pro is Settings-only). */
  plan: 'discover' | 'scale' | 'agency';
  /** Confirmed SetupIntent id (validated server-side). */
  setupIntentId: string;
  /** ISO-3166 alpha-2 country — feeds Stripe Tax. */
  country?: string;
}

/** `POST /api/v1/billing/start-trial` — onboarding card-upfront trial (US-028). */
export function useStartTrial(): UseMutationResult<StartTrialResponse, ApiError, StartTrialInput> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation<StartTrialResponse, ApiError, StartTrialInput>({
    mutationFn: (vars) => postJson<StartTrialResponse>(client, '/api/v1/billing/start-trial', vars),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: billingSubscriptionKey });
    },
  });
}

/** `POST /api/v1/billing/setup-intent` — Stripe Elements (US-197). */
export function useUpdatePaymentMethod(): UseMutationResult<SetupIntentResponse, ApiError, void> {
  const client = useApiClient();
  return useMutation<SetupIntentResponse, ApiError, void>({
    mutationFn: () => postJson<SetupIntentResponse>(client, '/api/v1/billing/setup-intent', {}),
  });
}

export type CrmProvider = 'hubspot' | 'pipedrive' | 'salesforce' | 'webhook';
export interface CrmConnectionSummary {
  id: string;
  provider: CrmProvider;
  status: 'active' | 'errored' | 'revoked';
  expiresAt: string | number | null;
  createdAt: string | number;
}
export interface CrmConnectionsResponse {
  connections: CrmConnectionSummary[];
  providers: { provider: CrmProvider; available: false }[];
}

export function useCrmConnections(): UseQueryResult<CrmConnectionsResponse, ApiError> {
  const client = useApiClient();
  return useQuery({
    queryKey: ['crm-connections'],
    queryFn: () => getJson<CrmConnectionsResponse>(client, '/api/v1/crm/connections'),
    retry: false,
  });
}

export function useConnectCrm(): UseMutationResult<never, ApiError, { provider: CrmProvider }> {
  const client = useApiClient();
  return useMutation({
    mutationFn: (vars) =>
      postJson<never>(client, `/api/v1/crm/connect/${encodeURIComponent(vars.provider)}`, {}),
  });
}

export function useDisconnectCrm(): UseMutationResult<{ ok: true }, ApiError, { id: string }> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      deleteJson<{ ok: true }>(client, `/api/v1/crm/connections/${encodeURIComponent(vars.id)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['crm-connections'] });
    },
  });
}

export type NotificationChannel = 'whatsapp' | 'telegram' | 'slack' | 'email';
export interface NotificationChannelSummary {
  id: string;
  channel: NotificationChannel;
  status: 'active' | 'errored' | 'revoked';
  createdAt: string | number;
}
export interface ChannelsResponse {
  channels: NotificationChannelSummary[];
  capabilities: { channel: NotificationChannel; available: false }[];
}

export function useChannels(): UseQueryResult<ChannelsResponse, ApiError> {
  const client = useApiClient();
  return useQuery({
    queryKey: ['channels'],
    queryFn: () => getJson<ChannelsResponse>(client, '/api/v1/channels'),
    retry: false,
  });
}

export function useConnectChannel(): UseMutationResult<
  never,
  ApiError,
  { channel: NotificationChannel }
> {
  const client = useApiClient();
  return useMutation({
    mutationFn: (vars) =>
      postJson<never>(client, `/api/v1/channels/${encodeURIComponent(vars.channel)}/connect`, {}),
  });
}

export function useDisconnectChannel(): UseMutationResult<
  { ok: true },
  ApiError,
  { channel: NotificationChannel }
> {
  const client = useApiClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars) =>
      deleteJson<{ ok: true }>(client, `/api/v1/channels/${encodeURIComponent(vars.channel)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['channels'] });
    },
  });
}
